import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, Check, X, Sparkles } from "lucide-react";

/**
 * First-run onboarding overlay — 3 steps, skippable per step but exit-locked
 * (clicking "Skip all" still marks the user onboarded so it never reappears).
 *
 * Step 1 — Welcome + mood baseline (1–5 emoji scale)
 * Step 2 — Pick interests (up to 10 tags)
 * Step 3 — Follow 5 suggested users
 */

const MOOD_EMOJI = ["😞", "🙁", "😐", "🙂", "😄"]; // 1..5

export default function OnboardingModal() {
  const { user, refresh } = useAuth();
  const shouldShow = !!user && !user.onboarded_at;
  const [step, setStep] = useState(0);
  const [mood, setMood] = useState(null);
  const [interests, setInterests] = useState([]);
  const [interestPool, setInterestPool] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [followed, setFollowed] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!shouldShow) return;
    api.get("/onboarding/interests")
      .then((r) => setInterestPool(r.data.interests || []))
      .catch((err) => console.warn("Failed to load interests", err));
    api.get("/discovery/suggested-users?limit=8")
      .then((r) => setSuggestions(r.data || []))
      .catch((err) => console.warn("Failed to load suggested users", err));
  }, [shouldShow]);

  const toggleInterest = (t) => {
    setInterests((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : prev.length >= 10 ? prev : [...prev, t]
    );
  };

  const followUser = async (id) => {
    if (followed[id]) return;
    setFollowed((p) => ({ ...p, [id]: true }));
    try { await api.post(`/users/${id}/follow`); } catch { setFollowed((p) => ({ ...p, [id]: false })); }
  };

  const finish = async (skipped = false) => {
    setBusy(true);
    try {
      const completed = [];
      if (mood != null) completed.push("mood");
      if (interests.length) completed.push("interests");
      if (Object.keys(followed).length) completed.push("follows");
      await api.post("/onboarding/complete", {
        interests,
        initial_mood: mood,
        completed_steps: completed,
      });
      if (typeof refresh === "function") await refresh();
      if (!skipped) toast.success("Welcome to HAEVEN");
    } catch (err) { console.warn("Onboarding completion failed (never blocks entry)", err); }
    finally { setBusy(false); }
  };

  const next = () => setStep((s) => s + 1);

  const totalSteps = 3;

  if (!shouldShow) return null;

  const node = (
    <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="brutal-card bg-card w-full max-w-2xl p-6 sm:p-8 relative">
        <button
          onClick={() => finish(true)}
          data-testid="onboarding-skip-all"
          className="absolute top-3 right-3 p-2 hover:opacity-70"
          aria-label="Skip onboarding"
        >
          <X size={18} strokeWidth={2.5} />
        </button>
        <StepDots count={totalSteps} current={step} />

        {step === 0 && (
          <Step1Welcome mood={mood} setMood={setMood} onNext={next} user={user} />
        )}
        {step === 1 && (
          <Step2Interests
            pool={interestPool}
            selected={interests}
            toggle={toggleInterest}
            onNext={next}
          />
        )}
        {step === 2 && (
          <Step3Follows
            suggestions={suggestions}
            followed={followed}
            onFollow={followUser}
            onFinish={() => finish(false)}
            busy={busy}
          />
        )}
      </div>
    </div>
  );
  return createPortal(node, document.body);
}

function StepDots({ count, current }) {
  return (
    <div className="flex gap-1.5 mb-5">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={`step-dot-${i}`}
          className={`h-1.5 flex-1 rounded-full transition-colors ${i <= current ? "bg-foreground" : "bg-muted"}`}
        />
      ))}
    </div>
  );
}

function Step1Welcome({ mood, setMood, onNext, user }) {
  return (
    <div data-testid="onboarding-step-mood">
      <h2 className="text-3xl mb-2 flex items-center gap-2">
        <Sparkles size={22} strokeWidth={2.5} /> Welcome to HAEVEN
      </h2>
      <p className="text-muted-foreground mb-6">
        Hi {user?.display_name?.split(" ")[0] || "there"} — quick three-step setup so HAEVEN feels like yours. First, how are you doing today?
      </p>
      <div className="flex justify-between gap-2 mb-8">
        {MOOD_EMOJI.map((e, i) => (
          <button
            key={e}
            onClick={() => setMood(i + 1)}
            data-testid={`onboarding-mood-${i + 1}`}
            className={`brutal-btn flex-1 py-4 text-3xl ${mood === i + 1 ? "bg-accent" : "bg-card"}`}
            aria-label={`Mood ${i + 1} of 5`}
          >
            {e}
          </button>
        ))}
      </div>
      <button
        onClick={onNext}
        data-testid="onboarding-next-1"
        className="brutal-btn w-full py-3 bg-foreground text-background inline-flex items-center justify-center gap-2"
      >
        {mood ? "Continue" : "Skip this step"} <ArrowRight size={16} strokeWidth={2.5} />
      </button>
    </div>
  );
}

function Step2Interests({ pool, selected, toggle, onNext }) {
  return (
    <div data-testid="onboarding-step-interests">
      <h2 className="text-3xl mb-2">What pulls you in?</h2>
      <p className="text-muted-foreground mb-6">
        Pick a few. We&apos;ll use these to suggest gentler corners of HAEVEN — never to ad-target you.
      </p>
      <div className="flex flex-wrap gap-2 mb-6 max-h-64 overflow-y-auto">
        {pool.map((t) => {
          const on = selected.includes(t);
          return (
            <button
              key={t}
              onClick={() => toggle(t)}
              data-testid={`onboarding-interest-${t}`}
              className={`px-3 py-1.5 text-sm rounded-full border-2 border-foreground transition-all ${on ? "bg-foreground text-background" : "bg-card"}`}
            >
              {on && <Check size={12} strokeWidth={3} className="inline mr-1 -mt-0.5" />}
              {t}
            </button>
          );
        })}
      </div>
      <button
        onClick={onNext}
        data-testid="onboarding-next-2"
        className="brutal-btn w-full py-3 bg-foreground text-background inline-flex items-center justify-center gap-2"
      >
        {selected.length ? `Continue (${selected.length})` : "Skip this step"} <ArrowRight size={16} strokeWidth={2.5} />
      </button>
    </div>
  );
}

function Step3Follows({ suggestions, followed, onFollow, onFinish, busy }) {
  return (
    <div data-testid="onboarding-step-follows">
      <h2 className="text-3xl mb-2">Find your circle</h2>
      <p className="text-muted-foreground mb-5">
        Follow a few people so your feed has something to start with. You can always change this later.
      </p>
      <div className="space-y-2 mb-6 max-h-72 overflow-y-auto">
        {suggestions.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No suggestions yet — your feed will fill as more people join.</p>
        )}
        {suggestions.map((u) => (
          <div key={u.id} className="brutal-card p-3 bg-background flex items-center gap-3" data-testid={`onboarding-suggested-${u.username}`}>
            <Avatar user={u} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{u.display_name}</div>
              <div className="text-xs text-muted-foreground truncate">
                @{u.username}
                {u.mutual_count > 0 && ` · ${u.mutual_count} mutual${u.mutual_count === 1 ? "" : "s"}`}
              </div>
            </div>
            <button
              onClick={() => onFollow(u.id)}
              data-testid={`onboarding-follow-${u.username}`}
              className={`brutal-btn px-3 py-1.5 text-sm ${followed[u.id] ? "bg-accent" : "bg-card"}`}
            >
              {followed[u.id] ? "Following" : "Follow"}
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={onFinish}
        disabled={busy}
        data-testid="onboarding-finish"
        className="brutal-btn w-full py-3 bg-foreground text-background disabled:opacity-50"
      >
        {busy ? "Finishing…" : "Enter HAEVEN"}
      </button>
    </div>
  );
}

function Avatar({ user }) {
  if (user.avatar_path) {
    const backend = process.env.REACT_APP_BACKEND_URL;
    return <img src={`${backend}/uploads/${user.avatar_path}`} alt="" className="w-10 h-10 rounded-full object-cover brutal-card-sm" />;
  }
  const initial = (user.display_name || user.username || "?")[0].toUpperCase();
  return <div className="w-10 h-10 rounded-full bg-accent brutal-card-sm flex items-center justify-center font-bold">{initial}</div>;
}
